import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Table, Tag, Button, Empty } from "antd";
import { CopyOutlined, CheckOutlined } from "@ant-design/icons";
import DynamicFieldRenderer from "../panels/DynamicFieldRender";
import { NodeData } from "../../types/workflow.types";
import "../../styles/modals/NodeConfigurationModal.css";

interface NodeConfigurationModalProps {
  visible: boolean;
  onClose: () => void;
  nodeData: NodeData | undefined;
  config: Record<string, any>;
  onSave: (config: Record<string, any>) => void;
  previousContextFields?: any[];
}

const CATEGORY_ICONS = {
  trigger: "⚡",
  condition: "🔍",
  action: "⚙️",
} as const;

export const NodeConfigurationModal: React.FC<NodeConfigurationModalProps> = ({
  visible,
  onClose,
  nodeData,
  config: initialConfig,
  onSave,
  previousContextFields = [],
}) => {
  const [config, setConfig] = useState<Record<string, any>>({});
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    setConfig(initialConfig || {});
  }, [initialConfig, nodeData?.nodeKey]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Escape" && visible) {
        onClose();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "s" && visible) {
        e.preventDefault();
        onSave(config);
        onClose();
      }
    };

    if (visible) {
      document.addEventListener("keydown", handleKeyPress);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [visible, onClose, onSave, config]);

  useEffect(() => {
    if (visible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [visible]);

  const handleCopyVariable = (varKey: string) => {
    navigator.clipboard.writeText(`{{${varKey}}}`);
    setCopiedField(varKey);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleFieldChange = (fieldKey: string, value: any) => {
    setConfig((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleSave = () => {
    onSave(config);
    onClose();
  };

  if (!visible || !nodeData) return null;

  let {
    category,
    label,
    nodeKey: key,
    fields = [],
    contextFields = [],
    dynamicData = {},
  } = nodeData;

  contextFields = contextFields?.filter((cf: any) => cf?.hideFromUI !== true);

  return createPortal(
    <div className="node-config-modal-overlay">
      {/* ❌ REMOVED onClick - no outside click to close */}
      <div className="node-config-modal">
        {/* Compact Header */}
        <div className="modal-header">
          <div className="header-left">
            <span className="modal-icon">
              {CATEGORY_ICONS[category as keyof typeof CATEGORY_ICONS] || "⚙️"}
            </span>
            <div className="header-text">
              <h3 className="modal-title">{label}</h3>
              <span className="modal-key">{key}</span>
            </div>
          </div>
          <button
            className="close-btn"
            onClick={onClose}
            aria-label="Close modal"
            title="Close (Esc)">
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          <ConfigAndContextContent
            fields={fields}
            config={config}
            dynamicData={dynamicData}
            contextFields={contextFields}
            previousContextFields={previousContextFields}
            copiedField={copiedField}
            onChange={handleFieldChange}
            onCopy={handleCopyVariable}
          />
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose} title="Cancel (Esc)">
            Cancel
          </button>
          <button
            className="btn-save"
            onClick={handleSave}
            title="Save and close">
            <span>Save Changes</span>
            <span className="keyboard-hint">Ctrl+S</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Rest of the components remain the same...
interface ConfigAndContextContentProps {
  fields: any[];
  config: Record<string, any>;
  dynamicData: Record<string, any>;
  contextFields: any[];
  previousContextFields: any[];
  copiedField: string | null;
  onChange: (key: string, value: any) => void;
  onCopy: (key: string) => void;
}

const ConfigAndContextContent: React.FC<ConfigAndContextContentProps> = ({
  fields,
  config,
  dynamicData,
  contextFields,
  previousContextFields,
  copiedField,
  onChange,
  onCopy,
}) => {
  const hasFields = fields && fields.length > 0;
  const hasPreviousContext =
    previousContextFields && previousContextFields.length > 0;
  const hasOutputContext = contextFields && contextFields.length > 0;
  const hasAnyContent = hasFields || hasPreviousContext || hasOutputContext;

  return (
    <div className="config-and-context-content">
      {hasFields && (
        <div className="config-section">
          <h3 className="section-title">
            <span className="section-icon">⚙️</span>
            Configuration
          </h3>
          <DynamicFieldRenderer
            fields={fields}
            config={config}
            dynamicData={dynamicData}
            previousContextFields={previousContextFields}
            onChange={onChange}
          />
        </div>
      )}

      {hasPreviousContext && (
        <div className="context-section">
          <h3 className="section-title">
            <span className="section-icon">📥</span>
            Available Context Variables
          </h3>
          <p className="context-description">
            Use these variables from previous nodes in your configuration
          </p>
          <ContextFieldsTable
            fields={previousContextFields}
            copiedField={copiedField}
            onCopy={onCopy}
            groupByNode={true}
          />
        </div>
      )}

      {hasOutputContext && (
        <div className="context-section">
          <h3 className="section-title">
            <span className="section-icon">📤</span>
            Output Variables
          </h3>
          <p className="context-description">
            These fields will be available to downstream nodes after execution
          </p>
          <ContextFieldsTable
            fields={contextFields}
            copiedField={copiedField}
            onCopy={onCopy}
            groupByNode={false}
          />
        </div>
      )}

      {!hasAnyContent && (
        <div className="empty-state">
          <Empty
            description="No configuration or context fields available"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      )}
    </div>
  );
};

interface ContextFieldsTableProps {
  fields: any[];
  copiedField: string | null;
  onCopy: (key: string) => void;
  groupByNode?: boolean;
}

const ContextFieldsTable: React.FC<ContextFieldsTableProps> = ({
  fields,
  copiedField,
  onCopy,
  groupByNode = false,
}) => {
  const groupedFields = useMemo(() => {
    if (!fields || fields.length === 0) {
      return {};
    }

    return fields.reduce((acc, field) => {
      const groupKey = groupByNode
        ? field.nodeLabel || "Unknown Node"
        : field.category || "Other";

      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(field);
      return acc;
    }, {} as Record<string, any[]>);
  }, [fields, groupByNode]);

  const columns = [
    {
      title: "Variable",
      dataIndex: "key",
      key: "key",
      width: "30%",
      render: (key: string) => (
        <code className="variable-code">{`{{${key}}}`}</code>
      ),
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      width: "25%",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "30%",
      render: (text: string) => (
        <span className="description-text">{text || "-"}</span>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center" as const,
      width: "15%",
      render: (_: any, record: any) => (
        <Button
          type={copiedField === record?.key ? "primary" : "default"}
          size="small"
          icon={
            copiedField === record?.key ? <CheckOutlined /> : <CopyOutlined />
          }
          onClick={() => onCopy(record.key)}>
          {copiedField === record.key ? "Copied" : "Copy"}
        </Button>
      ),
    },
  ];

  return (
    <div className="context-table-wrapper">
      {Object.entries(groupedFields).map(([groupName, items]: [any, any]) => (
        <div key={groupName} className="category-section">
          <div className="category-header">
            <span className="category-icon">
              {items[0]?.icon || items[0]?.nodeIcon || "📋"}
            </span>
            <span className="category-name">{groupName}</span>
            <Tag color="blue">{items.length}</Tag>
          </div>
          <Table
            columns={columns}
            dataSource={items}
            rowKey={(record) => `${record.key}-${record.nodeId || ""}`}
            pagination={false}
            size="small"
            bordered={false}
            className="context-fields-table"
          />
        </div>
      ))}
    </div>
  );
};

export default NodeConfigurationModal;
